import { Button, ValidationMessage } from '@huntington/honeycomb';
import { Collapse, Stack, Typography } from '@mui/material';
import {
	useCallback,
	useEffect,
	useState,
	type ChangeEventHandler,
	type FocusEventHandler,
	type SyntheticEvent,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCreatePasswordMutation } from '../../api/user.api';
import { PasswordInput } from '../../components/Form/PasswordInput';
import {
	PasswordRequirements,
	SuccessMessage,
	validatePassword,
} from '../../components/Form/PasswordRequirements';
import SpinnerModal from '../../components/Modals/SpinnerModal/SpinnerModal';
import {
	CANCEL,
	CONFIRM_PASSWORD,
	CREATE_NEW_USER_PASSWORD_TITLE,
	NEW_PASSWORD,
	NO_PASSWORD_ERROR,
	PASSWORD_REQUIREMENT_MATCHING,
	PASSWORD_REQUIREMENT_MEETS,
	PASSWORD_REQUIREMENT_NOT_MATCHING,
	PASSWORD_REQUIREMENT_NOT_MET,
	RESET_PASSWORD_DESCRIPTION,
	SUBMIT,
} from '../../constants/messages';
import {
	ROUTE_ENROLLMENT,
	ROUTE_DISCLOSURE_ACCEPTANCE_SUCCESS,
	ROUTE_ERROR,
	ROUTE_LOGIN,
} from '../../router/router';

export default function CreatePassword() {
	const navigate = useNavigate();
	const location = useLocation();
	const email = location.state?.email || '';
	const resetToken = location.state?.resetToken || '';
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [touchedPassword, setTouchedPassword] = useState(false);
	const [inputOneError, setInputOneError] = useState('');
	const [inputTwoError, setInputTwoError] = useState('');
	const [isLoading, setIsLoading] = useState(false);

	const [createPassword, { isSuccess: hasResetPassword, isError: hasErroredOnPasswordReset }] =
		useCreatePasswordMutation();

	const handleCancel = useCallback(() => {
		navigate(ROUTE_LOGIN);
	}, [navigate]);

	const handlePrimary = useCallback(
		(e: SyntheticEvent) => {
			e.preventDefault();

			const { allValid } = validatePassword(newPassword);

			if (newPassword === '') {
				setInputOneError(NO_PASSWORD_ERROR);
				return;
			}

			if (!allValid) {
				setInputOneError(PASSWORD_REQUIREMENT_NOT_MET);
				return;
			}
			if (newPassword !== confirmPassword) {
				setInputTwoError(PASSWORD_REQUIREMENT_NOT_MATCHING);
				return;
			}
			setInputOneError('');
			setInputTwoError('');
			setIsLoading(true);
			createPassword({ emailId: email, password: newPassword, token: resetToken });
		},
		[email, resetToken, newPassword, confirmPassword, createPassword]
	);

	useEffect(() => {
		if (hasResetPassword) {
			setIsLoading(false);
			navigate(`${ROUTE_ENROLLMENT}/${ROUTE_DISCLOSURE_ACCEPTANCE_SUCCESS}`);
		}
	}, [hasResetPassword, navigate]);

	useEffect(() => {
		if (hasErroredOnPasswordReset) {
			setIsLoading(false);
			navigate(`${ROUTE_ENROLLMENT}/${ROUTE_ERROR}`);
		}
	}, [hasErroredOnPasswordReset, navigate]);

	const handleNewPasswordChange: ChangeEventHandler<HTMLInputElement> = ({ target: { value } }) => {
		if (value && !touchedPassword) {
			setTouchedPassword(true);
		}
		setNewPassword(value);

		// Error checking
		let errorMessage = '';
		let confirmErrorMessage = '';

		if (value !== confirmPassword) {
			confirmErrorMessage = PASSWORD_REQUIREMENT_NOT_MATCHING;
		}
		const { allValid } = validatePassword(value);
		if (!allValid) {
			errorMessage = PASSWORD_REQUIREMENT_NOT_MET;
		}
		if (!value) {
			errorMessage = NO_PASSWORD_ERROR;
		}
		setInputOneError(errorMessage);
		setInputTwoError(confirmErrorMessage);
	};

	const handlePasswordBlur: FocusEventHandler<HTMLInputElement> = ({ target: { id, value } }) => {
		setTouchedPassword(true);
		if (!value) {
			if (id === 'new-password') {
				setInputOneError(NO_PASSWORD_ERROR);
			}
			if (id === 'confirm-password') {
				setInputTwoError(NO_PASSWORD_ERROR);
			}
		}
	};

	const handleConfirmPasswordChange: ChangeEventHandler<HTMLInputElement> = ({
		target: { value },
	}) => {
		if (value && !touchedPassword) {
			setTouchedPassword(true);
		}
		setConfirmPassword(value);

		// Error checking
		if (!value) {
			setInputTwoError(NO_PASSWORD_ERROR);
			return;
		}
		if (value !== newPassword) {
			setInputTwoError(PASSWORD_REQUIREMENT_NOT_MATCHING);
			return;
		}
		setInputTwoError('');
	};

	return (
		<Stack>
			{isLoading && <SpinnerModal />}
			<form onSubmit={handlePrimary}>
				<Stack>
					<Typography
						component="h1"
						variant="h1"
						sx={{ font: 'bold 2.25rem "Huntington Serif", serif' }}
					>
						{CREATE_NEW_USER_PASSWORD_TITLE}
					</Typography>
					<Typography sx={{ padding: '1.5rem 0 1rem' }}>{RESET_PASSWORD_DESCRIPTION}</Typography>

					<PasswordInput
						error={!!inputOneError}
						id="new-password"
						label={NEW_PASSWORD}
						onBlur={handlePasswordBlur}
						onChange={handleNewPasswordChange}
						value={newPassword}
					/>

					<Collapse in={!!inputOneError} mountOnEnter unmountOnExit>
						<ValidationMessage id="input-error" sx={{ mt: '0.25rem' }}>
							<span>{inputOneError}</span>
						</ValidationMessage>
					</Collapse>

					<Collapse in={touchedPassword} mountOnEnter unmountOnExit>
						<PasswordRequirements password={newPassword} />
					</Collapse>

					<Collapse in={touchedPassword && !inputOneError} mountOnEnter unmountOnExit>
						<SuccessMessage>{PASSWORD_REQUIREMENT_MEETS}</SuccessMessage>
					</Collapse>

					<PasswordInput
						error={!!inputTwoError}
						id="confirm-password"
						label={CONFIRM_PASSWORD}
						onBlur={handlePasswordBlur}
						onChange={handleConfirmPasswordChange}
						sx={{ marginTop: '1rem' }}
						value={confirmPassword}
					/>

					<Collapse in={!!inputTwoError} mountOnEnter unmountOnExit>
						<ValidationMessage id="input-two-error" sx={{ mt: '0.25rem' }}>
							<span>{inputTwoError}</span>
						</ValidationMessage>
					</Collapse>

					<Collapse
						in={!inputTwoError && !!confirmPassword && newPassword === confirmPassword}
						mountOnEnter
						unmountOnExit
					>
						<SuccessMessage>{PASSWORD_REQUIREMENT_MATCHING}</SuccessMessage>
					</Collapse>
				</Stack>

				<Stack
					alignItems="center"
					direction="row"
					justifyContent={'flex-end'}
					spacing={2}
					paddingTop="3.25rem"
				>
					<Button onClick={handleCancel} type="button" variant="outlined">
						{CANCEL}
					</Button>
					<Button onClick={handlePrimary} type="submit">
						{SUBMIT}
					</Button>
				</Stack>
			</form>
		</Stack>
	);
}
